/**
 * Live Pricing Simulator — sticky side panel.
 *
 * Inputs: Brand, Category, Style (SKU), Color, Size, Cost, Quantity,
 * Connector (supplier slug), Source Type, Customer Category.
 *
 * On every change: debounced POST to /api/pricing/quote and renders:
 *   • unit_price, total, margin %
 *   • rule applied (name)
 *   • strategy
 *   • fallback flag (badge if no rule matched)
 *   • applied clamps (badges for min_profit / max_discount when they fired)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";

const AXIOS_OPTS = { withCredentials: true };

const SOURCE_TYPES = ["supplier", "internal", "custom", "service"];

function emptySim() {
  return {
    connector: "",
    source_type: "supplier",
    brand: "",
    category: "",
    style: "",
    sku: "",
    color: "",
    size: "",
    customer_category_id: "",
    cost: "",
    quantity: "1",
  };
}

export default function PricingSimulator({ meta, rulesVersion }) {
  const { t } = useI18n();
  const [inputs, setInputs] = useState(emptySim);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const canQuote = useMemo(() => {
    return inputs.cost !== "" && Number(inputs.quantity || 0) > 0;
  }, [inputs]);

  const runQuote = useCallback(async () => {
    if (!canQuote) {
      setQuote(null);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        connector: inputs.connector || null,
        source_type: inputs.source_type || null,
        brand: inputs.brand || null,
        category: inputs.category || null,
        style: inputs.style || null,
        sku: inputs.sku || null,
        color: inputs.color || null,
        size: inputs.size || null,
        customer_category_id: inputs.customer_category_id || null,
        cost: Number(inputs.cost || 0),
        quantity: Number(inputs.quantity || 1),
      };
      const res = await axios.post(`${API}/pricing/quote`, payload, AXIOS_OPTS);
      setQuote(res.data);
    } catch (e) {
      setQuote({ _error: e?.response?.data?.detail || "Error" });
    } finally {
      setBusy(false);
    }
  }, [inputs, canQuote]);

  // Debounced auto-run on any change (including rules changing externally).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runQuote, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, rulesVersion]);

  const update = (k, v) => setInputs((prev) => ({ ...prev, [k]: v }));
  const reset = () => setInputs(emptySim());

  return (
    <div
      data-testid={DASHBOARD.pricingSimulator}
      className="bg-white border border-[var(--border)] shadow-sm"
    >
      <div className="border-b border-[var(--border)] p-4 bg-black text-white">
        <div className="label-overline text-white/70">SIM · LIVE</div>
        <h3 className="font-display text-lg font-black tracking-tight">{t("pricing_sim_title")}</h3>
        <p className="text-[0.7rem] text-white/60 mt-1">{t("pricing_sim_subtitle")}</p>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field
            label={t("pricing_filter_connector")}
            testId={DASHBOARD.pricingSimConnector}
            value={inputs.connector}
            onChange={(v) => update("connector", v)}
            options={meta.connectors?.map((c) => ({ value: c.slug, label: c.label }))}
            allowFreeText
          />
          <SelectField
            label={t("pricing_filter_source_type")}
            testId={DASHBOARD.pricingSimSourceType}
            value={inputs.source_type}
            onChange={(v) => update("source_type", v)}
            options={SOURCE_TYPES.map((s) => ({ value: s, label: s }))}
          />
          <Field
            label={t("pricing_filter_brand")}
            testId={DASHBOARD.pricingSimBrand}
            value={inputs.brand}
            onChange={(v) => update("brand", v)}
            options={meta.brands?.map((b) => ({ value: b.name, label: b.name }))}
            allowFreeText
          />
          <Field
            label={t("pricing_filter_category")}
            testId={DASHBOARD.pricingSimCategory}
            value={inputs.category}
            onChange={(v) => update("category", v)}
            options={meta.categories?.map((c) => ({ value: c.name, label: c.name }))}
            allowFreeText
          />
          <Field
            label={t("pricing_filter_style")}
            testId={DASHBOARD.pricingSimStyle}
            value={inputs.style}
            onChange={(v) => update("style", v)}
          />
          <Field
            label={t("pricing_filter_sku")}
            testId={DASHBOARD.pricingSimSku}
            value={inputs.sku}
            onChange={(v) => update("sku", v)}
          />
          <Field
            label={t("pricing_filter_color")}
            testId={DASHBOARD.pricingSimColor}
            value={inputs.color}
            onChange={(v) => update("color", v)}
            options={meta.colors?.map((c) => ({ value: c.name, label: c.name }))}
            allowFreeText
          />
          <Field
            label={t("pricing_filter_size")}
            testId={DASHBOARD.pricingSimSize}
            value={inputs.size}
            onChange={(v) => update("size", v)}
            options={meta.sizes?.map((s) => ({ value: s.name, label: s.name }))}
            allowFreeText
          />
          <SelectField
            label={t("pricing_filter_customer_category")}
            testId={DASHBOARD.pricingSimCustomerCategory}
            value={inputs.customer_category_id}
            onChange={(v) => update("customer_category_id", v)}
            options={(meta.customer_categories || []).map((c) => ({ value: c.id, label: c.name }))}
          />
          <div>
            <Label>{t("pricing_sim_qty_placeholder")}</Label>
            <input
              data-testid={DASHBOARD.pricingSimQuantity}
              type="number"
              min="1"
              value={inputs.quantity}
              onChange={(e) => update("quantity", e.target.value)}
              className="w-full border border-[var(--border)] px-2 py-1.5 text-sm"
            />
          </div>
          <div className="col-span-2">
            <Label>{t("pricing_sim_cost_placeholder")} *</Label>
            <input
              data-testid={DASHBOARD.pricingSimCost}
              type="number"
              step="0.01"
              value={inputs.cost}
              onChange={(e) => update("cost", e.target.value)}
              placeholder="0.00"
              className="w-full border-2 border-black px-2 py-1.5 text-sm font-mono-tech"
            />
          </div>
        </div>

        <button
          data-testid={DASHBOARD.pricingSimResetBtn}
          onClick={reset}
          className="w-full font-mono-tech text-[0.7rem] border border-[var(--border)] py-1.5 hover:bg-black hover:text-white transition-colors"
        >
          {t("pricing_sim_reset")}
        </button>

        {/* Results */}
        <div className="border-t border-[var(--border)] pt-3 space-y-2 mt-3">
          <div className="label-overline">
            {t("pricing_sim_result")} {busy ? "·..." : ""}
          </div>
          {!quote && (
            <div className="text-xs text-[var(--text-secondary)] italic">
              Ingresa costo y cantidad para ver el precio.
            </div>
          )}
          {quote && quote._error && (
            <div className="text-xs text-red-700">{quote._error}</div>
          )}
          {quote && !quote._error && (
            <>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-[var(--text-secondary)]">{t("pricing_sim_unit_price")}</span>
                <span
                  data-testid={DASHBOARD.pricingSimResultUnitPrice}
                  className="font-mono-tech text-2xl font-black"
                >
                  ${Number(quote.unit_price || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--text-secondary)]">{t("pricing_sim_total")}</span>
                <span data-testid={DASHBOARD.pricingSimResultTotal} className="font-mono-tech font-bold">
                  ${Number(quote.total_price || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--text-secondary)]">{t("pricing_sim_margin")}</span>
                <span
                  data-testid={DASHBOARD.pricingSimResultMargin}
                  className={`font-mono-tech font-bold ${
                    (quote.margin_pct || 0) < 20 ? "text-red-700" : "text-emerald-700"
                  }`}
                >
                  {quote.margin_pct != null ? `${quote.margin_pct.toFixed(2)}%` : "—"}
                </span>
              </div>
              <div className="text-xs">
                <div className="text-[var(--text-secondary)]">{t("pricing_sim_rule")}</div>
                <div
                  data-testid={DASHBOARD.pricingSimResultRuleName}
                  className="font-display font-bold text-sm"
                >
                  {quote.rule_name || "—"}
                </div>
                <div
                  data-testid={DASHBOARD.pricingSimResultStrategy}
                  className="font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)]"
                >
                  {quote.strategy}
                  {quote.strategy_price != null && quote.strategy_price !== quote.unit_price && (
                    <> · strategy={"$"}{quote.strategy_price.toFixed(2)} → final={"$"}{Number(quote.unit_price).toFixed(2)}</>
                  )}
                </div>
              </div>

              {quote.fallback && (
                <div
                  data-testid={DASHBOARD.pricingSimResultFallback}
                  className="text-[0.7rem] font-mono-tech font-bold tracking-wider px-2 py-1 bg-amber-100 text-amber-900 border border-amber-300"
                >
                  ⚠ {t("pricing_sim_fallback")}
                </div>
              )}

              {(quote.applied_clamps || []).length > 0 && (
                <div
                  data-testid={DASHBOARD.pricingSimResultClamps}
                  className="flex flex-wrap gap-1"
                >
                  {(quote.applied_clamps || []).includes("min_profit") && (
                    <span className="text-[0.65rem] font-mono-tech font-bold tracking-wider px-1.5 py-0.5 bg-red-600 text-white">
                      🛡 {t("pricing_sim_clamp_min_profit")}
                    </span>
                  )}
                  {(quote.applied_clamps || []).includes("max_discount") && (
                    <span className="text-[0.65rem] font-mono-tech font-bold tracking-wider px-1.5 py-0.5 bg-orange-600 text-white">
                      🛡 {t("pricing_sim_clamp_max_discount")}
                    </span>
                  )}
                </div>
              )}

              {quote.breakdown && quote.breakdown.length > 0 && (
                <div className="border-t border-dashed border-[var(--border)] pt-2">
                  <div className="label-overline mb-1">Tiers</div>
                  <div className="space-y-1">
                    {quote.breakdown.map((b, i) => (
                      <div
                        key={i}
                        className={`flex justify-between text-xs font-mono-tech ${
                          b.active ? "font-bold text-black" : "text-[var(--text-secondary)]"
                        }`}
                      >
                        <span>
                          {b.active ? "★" : "·"} {b.label || `${b.min_quantity}+`}
                        </span>
                        <span>${Number(b.unit_price).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[0.65rem] font-mono-tech tracking-wider text-[var(--text-secondary)] mb-0.5">
      {children}
    </label>
  );
}

function SelectField({ label, testId, value, onChange, options }) {
  return (
    <div>
      <Label>{label}</Label>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[var(--border)] px-2 py-1.5 text-sm bg-white"
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Field({ label, testId, value, onChange, options, allowFreeText }) {
  const dlId = testId ? `${testId}-dl` : undefined;
  const hasList = options && options.length > 0 && allowFreeText;
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testId}
        type="text"
        list={hasList ? dlId : undefined}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[var(--border)] px-2 py-1.5 text-sm"
      />
      {hasList && (
        <datalist id={dlId}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </datalist>
      )}
    </div>
  );
}
